import { type datadogRum } from '@datadog/browser-rum';
import Observability, { LDObserve } from '@launchdarkly/observability';
import SessionReplay, { LDRecord } from '@launchdarkly/session-replay';
import { type AsyncProviderConfig, asyncWithLDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import {
  type ComponentType,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  type EnvironmentDetails,
  useEnvironmentDetailsQuery,
} from '@dutchie-pos/requests/admin/use-environment-details-query';
import { cleanLdKey, getDatadogTraceUrl, subscribeReassertRecording } from '@dutchie-pos/utils';

import { useUser } from 'src/queries/curbside/user-details';
import { useAppSelector } from 'src/utils';
import { setLDClient } from 'src/utils/helpers/ld-client';
import { logger } from 'src/utils/logger';

import { type AppReady } from '..';
import { useDatadog } from './datadog';

async function createLaunchDarklyProvider(
  envDetails: Pick<EnvironmentDetails, 'LaunchDarklyClientKey' | 'Region' | 'LaunchDarklyRelayPos' | 'UseLaunchDarklyRelayForEvents'>,
  datadog?: typeof datadogRum,
) {
  const { LaunchDarklyClientKey, Region, LaunchDarklyRelayPos, UseLaunchDarklyRelayForEvents } = envDetails;

  const ldProviderParam: AsyncProviderConfig = {
    clientSideID: LaunchDarklyClientKey ?? '',
    context: {
      kind: 'user',
      key: 'eng-internal',
      email: 'engineering@dutchie.com',
      region: Region ?? '',
      host: window.location.hostname,
    },
    reactOptions: {
      useCamelCaseFlagKeys: false,
      sendEventsOnFlagRead: true,
    },
    options: {
      ...(LaunchDarklyRelayPos && LaunchDarklyRelayPos !== ''
        ? {
            baseUrl: LaunchDarklyRelayPos,
            streamUrl: LaunchDarklyRelayPos,
            ...(UseLaunchDarklyRelayForEvents && { eventsUrl: LaunchDarklyRelayPos }),
          }
        : {}),
      plugins: [
        new Observability({
          manualStart: true,
          tracingOrigins: true,
          networkRecording: {
            enabled: true,
            recordHeadersAndBody: true,
            // POS-19460: never record credential-bearing auth request/response bodies in Replay.
            urlBlocklist: ['/curbside/login', '/posv3/user/employeelogin', '/posv3/user/resetpassword'],
            // POS-19460 review: SessionId (the app's bearer session token) rides in the body of many
            // non-auth requests too (e.g. queries/curbside/user-details.ts), which the URL blocklist
            // above doesn't cover. Redact it globally by body key regardless of endpoint.
            // POS-19460 review (blocking Bug fix): @launchdarkly/observability lowercases each body
            // key before checking membership in this list as-is -- a mixed-case entry here never
            // matches anything. Must be lowercase.
            networkBodyKeysToRedact: ['sessionid'],
          },
        }),
        new SessionReplay({
          manualStart: true,
          privacySetting: 'none',
          blockSelector: '[data-testid="manager-pin-input"]',
        }),
      ],
    },
  };

  if (!!datadog) {
    ldProviderParam.options = {
      ...ldProviderParam.options,
      inspectors: [
        {
          type: 'flag-used',
          name: 'dd-inspector',
          method: (key, detail) => {
            datadog.addFeatureFlagEvaluation(cleanLdKey(key), detail.value);
          },
        },
      ],
    };
  }

  return await asyncWithLDProvider(ldProviderParam);
}

export type LaunchDarklyProviderProps = {
  children: React.ReactNode;
  setReady: Dispatch<SetStateAction<AppReady>>;
};

function LaunchDarklyIdentity({ children, setReady }: LaunchDarklyProviderProps) {
  // use this to set identity
  const [ldReady, setLdReady] = useState(false);
  const client = useLDClient();
  const { data: user } = useUser();
  const session = useAppSelector((state) => state.user.session);

  // Capture the LD client in the lightweight holder so non-React code (e.g. the
  // outbound request-header builder) can read flag variations without a hook.
  useEffect(() => {
    setLDClient(client ?? undefined);
  }, [client]);

  useEffect(() => {
    if (user && client) {
      client.identify({
        key: user.UserName,
        kind: 'user',
        email: user.UserName,
        locId: user.LocId,
        lspId: user.LspId,
        region: user.Region,
        host: globalThis.location.hostname,
      });

      // Update LaunchDarkly session properties for observability/replay
      LDRecord.addSessionProperties({
        application: 'mobile-checkout',
        locId: String(user.LocId),
        lspId: String(user.LspId),
        region: user.Region,
        userId: user.UserId,
        // Datadog APM trace URL keyed off the app session; no-ops to '' when missing.
        datadogTraceUrl: getDatadogTraceUrl(session),
      });
    }
  }, [user, client, session]);

  useEffect(() => {
    LDObserve.start();
    LDRecord.start({ silent: true });

    return subscribeReassertRecording(LDRecord, {
      isEnabled: () => true,
      onError: (error) => logger.error(error, { phase: 'ld-session-replay-reassert' }),
    });
  }, []);

  useEffect(() => {
    setLdReady(true);
    setReady((prev) => ({
      ...prev,
      launchdarkly: true,
    }));
  }, []);

  if (!ldReady) {
    return null;
  }

  return children;
}

// https://github.com/launchdarkly/react-client-sdk/blob/main/src/asyncWithLDProvider.tsx
export function LaunchDarklyRoot({ children, setReady }: LaunchDarklyProviderProps) {
  const [ldProviderCreated, setLdProviderCreated] = useState(false);
  const provider = useRef<ComponentType<{ children?: ReactNode }> | null>(null);
  const { data: envDetails, isLoading, error } = useEnvironmentDetailsQuery();
  const { datadog } = useDatadog();

  const isLoadingEnvDetails = isLoading || !envDetails?.Data;

  useEffect(() => {
    async function init() {
      if (envDetails?.Data && !provider.current && !ldProviderCreated) {
        provider.current = await createLaunchDarklyProvider(envDetails.Data, datadog);
        // we need to set the state to trigger a re-render
        setLdProviderCreated(true);
      }
    }
    void init();
  }, [envDetails?.Data, provider, ldProviderCreated, datadog]);

  const LaunchDarklyProvider = provider.current;

  return (
    <>
      {!isLoadingEnvDetails && LaunchDarklyProvider && ldProviderCreated && (
        <LaunchDarklyProvider>
          <LaunchDarklyIdentity setReady={setReady}>{children}</LaunchDarklyIdentity>
        </LaunchDarklyProvider>
      )}
      {!isLoading && error && <div>Unable to connect to /api</div>}
    </>
  );
}
